import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { Maintenance, RegistrationAgreement, AccountInformationModal } from '@/components';
import { useAuthStore } from '@/zustand/auth/store.ts';
import { NavigationPath } from '@/router/navigation-path.ts';
import { parseJwt, jsonTryParse } from '@/utility';
import { useFeatureFlag } from 'configcat-react';
import { CONFIGCAT_KEYS } from '@/constants/configcat-keys.ts';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useAppSettings } from '@/zustand/app-settings/store.ts';
import LoadingSpinner from '@/components/loading-spinner';
import { DefaultPagesForServiceTypes, NavigationRuleName, RestrictedPages } from './auth-guard.constants.ts';
import { MeetingProvider } from '@/context/meeting-context';
import ExceptionManager from '@/managers/exception-manager.tsx';
import * as Sentry from '@sentry/react';
import { SERVICES_LIST } from '@/constants';
import { NotificationProvider } from '@/context/notification-context';
import { ManualCalendarSlotUpdateRequestLiveServiceTypeEnum } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import BrazeUtil from '@/utility/braze-util.ts';
import { syncBrazeExpertAttributes } from '@/utility/braze-attributes.ts';
import { isWebView, sendMessageToWebView } from '@/utility/webview.ts';
import { PostMessage } from '@/constants/post-message.ts';

export const AuthGuard: React.FC = () => {
	const { ExpertControllerApi, ExpertApplicationControllerApi, CalendarControllerApi, ContractControllerApi } = useApi();
	const { data: authData } = useAuthStore();
	const { data: appSettingsData, setData: setAppSettingsData } = useAppSettings();
	const location = useLocation();

	const userId = useMemo(() => (authData?.access_token ? parseJwt(authData?.access_token)?.sub : ''), [authData?.access_token]);
	const expertId = useMemo(() => (authData?.access_token ? parseJwt(authData?.access_token)?.additional_info?.expert_id : null), [authData?.access_token]);

	const { value: reviewerExpertIdListRaw } = useFeatureFlag(CONFIGCAT_KEYS.REVIEWER_EXPERT_ID_LIST, '[]');
	const isReviewerExpert = useMemo(
		() => !!expertId && jsonTryParse(reviewerExpertIdListRaw as string).some((id: number) => id === parseInt(String(expertId))),
		[expertId, reviewerExpertIdListRaw]
	);

	const [isLoadingContractLog, setIsLoadingContractLog] = useState<boolean>(false);
	const [isOpenContractLog, setIsOpenContractLog] = useState<boolean>(false);

	const isApplicant = useMemo(() => {
		if (authData?.access_token) {
			return parseJwt(authData?.access_token)?.resource_access?.['live-expert-applicant']?.roles?.includes('Expert_Applicant');
		}

		return false;
	}, [authData?.access_token]);

	const { data: completedApplicationSteps, isLoading: completedApplicationStepsIsLoading } = useQueryProvider(
		[QUERY_KEYS.getApplicationStepProgress],
		() => ExpertApplicationControllerApi.getApplicationStepProgress(),
		{
			refetchOnWindowFocus: false,
			select: ({ data }) => {
				if (data?.data) {
					return data?.data?.find(step => !step.isCompleted);
				}
			},
			enabled: !!isApplicant
		}
	);

	const { data: expertDetailInfo, isLoading: expertDetailInfoIsLoading } = useQueryProvider(
		[QUERY_KEYS.getLoggedInExpertDetailInfo, userId],
		() => ExpertControllerApi.getLoggedInExpertDetailInfo(),
		{
			select: ({ data }) => data.data,
			refetchOnWindowFocus: false,
			onSuccess: response => {
				setAppSettingsData({
					...appSettingsData,
					isOpenBankInformation: !response?.isPaymentInfoCreated
				});

				Sentry.setUser({
					id: response?.expert?.id,
					username: response?.expert?.name || '' + response?.expert?.surname || '',
					phone: response?.expert?.phone,
					email: response?.expert?.email
				});

				if (response) {
					syncBrazeExpertAttributes(response);
				}
			},
			enabled: !isApplicant && !!authData?.access_token
		}
	);

	const haveRecurringCalendar = useMemo(() => {
		return expertDetailInfo?.serviceList?.find(service => service.liveServiceTypeId === 4);
	}, [expertDetailInfo]);

	const haveGotITRecurringCalendar = useMemo(() => {
		return expertDetailInfo?.serviceList?.find(service => service.liveServiceTypeId === 6);
	}, [expertDetailInfo]);

	const hasSolver = useMemo(() => {
		const _hasSolver = expertDetailInfo?.serviceList?.find(service => service.liveServiceTypeId === 1);

		return _hasSolver && expertDetailInfo?.serviceList?.length === 1;
	}, [expertDetailInfo]);

	const hasNoServices = useMemo(() => {
		if (!expertDetailInfo) return false;
		return !expertDetailInfo.serviceList?.length;
	}, [expertDetailInfo]);

	const { data: weekCounts, isLoading: LoadingWeekCounts } = useQueryProvider(
		[QUERY_KEYS.countExpertRepetitiveSlotDayByDay, ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.OneOnOneCoaching],
		() => CalendarControllerApi.countExpertRepetitiveSlotDayByDay(ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.OneOnOneCoaching),
		{
			select: ({ data }) => data.data,
			refetchOnWindowFocus: false,
			enabled: !isApplicant && expertDetailInfo?.expert?.applicationStatus === 'APPROVED' && !completedApplicationStepsIsLoading && !!haveRecurringCalendar
		}
	);

	const { data: gotItWeekCounts, isLoading: LoadingGotItWeekCounts } = useQueryProvider(
		[QUERY_KEYS.countExpertRepetitiveSlotDayByDay, ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.OneOnOneLecture],
		() => CalendarControllerApi.countExpertRepetitiveSlotDayByDay(ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.OneOnOneLecture),
		{
			select: ({ data }) => data.data,
			refetchOnWindowFocus: false,
			enabled: !isApplicant && expertDetailInfo?.expert?.applicationStatus === 'APPROVED' && !completedApplicationStepsIsLoading && !!haveGotITRecurringCalendar
		}
	);

	const isInRestrictedPage = useMemo(() => {
		if (!expertDetailInfo?.serviceList?.length) return false;

		const approvedServices = expertDetailInfo.serviceList
			.filter(service => service.expertServiceStatus === 'APPROVED')
			.map(service => SERVICES_LIST[service.liveServiceTypeId as keyof typeof SERVICES_LIST]);

		for (const service of approvedServices) {
			if (!RestrictedPages[service as keyof typeof RestrictedPages]?.includes(location.pathname)) {
				return false;
			}
		}

		return approvedServices.length > 0;
	}, [location.pathname, expertDetailInfo]);

	const getDefaultPage = useMemo(() => {
		if (!expertDetailInfo?.serviceList?.length) return location.pathname;

		const approvedServices = expertDetailInfo.serviceList
			.filter(service => service.expertServiceStatus === 'APPROVED')
			.map(service => SERVICES_LIST[service.liveServiceTypeId as keyof typeof SERVICES_LIST]);

		if (approvedServices.length === 0) return location.pathname;

		if (isInRestrictedPage) {
			for (const service of approvedServices) {
				const defaultPage = DefaultPagesForServiceTypes[service as keyof typeof DefaultPagesForServiceTypes];
				if (defaultPage) {
					return defaultPage;
				}
			}
		}

		return location.pathname;
	}, [expertDetailInfo, isInRestrictedPage]);

	const navigationRules = useMemo(
		() => [
			{
				condition: completedApplicationStepsIsLoading || expertDetailInfoIsLoading,
				redirect: () => <LoadingSpinner loadingText={'Yükleniyor'} />,
				name: NavigationRuleName.IS_LOADING
			},
			{
				condition: !authData?.access_token,
				redirect: () => (
					<Navigate
						to={NavigationPath.login}
						replace
					/>
				),
				name: NavigationRuleName.NOT_AUTHENTICATED
			},
			{
				condition: isApplicant && location.pathname !== NavigationPath.applicationReview && !completedApplicationSteps,
				redirect: () => (
					<Navigate
						to={NavigationPath.applicationReview}
						replace
					/>
				),
				name: NavigationRuleName.IS_APPLICANT_REVIEW
			},
			{
				condition: location.pathname !== NavigationPath.applicationOnboarding && completedApplicationSteps && isApplicant && expertDetailInfo?.expert?.applicationStatus !== 'APPROVED',
				redirect: () => (
					<Navigate
						to={NavigationPath.applicationOnboarding}
						replace
					/>
				),
				name: NavigationRuleName.IS_APPLICANT_ONBOARDING
			},
			{
				condition:
					weekCounts &&
					weekCounts?.length <= 0 &&
					!LoadingWeekCounts &&
					!!haveRecurringCalendar &&
					location.pathname !== NavigationPath.coachRecurringCalendar &&
					location.pathname !== NavigationPath.gotItRecurringCalendar &&
					location.pathname !== NavigationPath.profile,
				redirect: () => (
					<Navigate
						to={NavigationPath.coachRecurringCalendar}
						replace
					/>
				),
				name: NavigationRuleName.NEEDS_RECURRING_CALENDAR_SETUP
			},
			{
				condition:
					gotItWeekCounts &&
					gotItWeekCounts?.length <= 0 &&
					!LoadingGotItWeekCounts &&
					!!haveGotITRecurringCalendar &&
					location.pathname !== NavigationPath.gotItRecurringCalendar &&
					location.pathname !== NavigationPath.coachRecurringCalendar &&
					location.pathname !== NavigationPath.profile,
				redirect: () => (
					<Navigate
						to={NavigationPath.gotItRecurringCalendar}
						replace
					/>
				),
				name: NavigationRuleName.NEEDS_RECURRING_CALENDAR_SETUP
			},
			{
				condition: isInRestrictedPage && getDefaultPage !== location.pathname,
				redirect: () => (
					<Navigate
						to={getDefaultPage}
						replace
					/>
				),
				name: NavigationRuleName.IS_RESTRICTED
			},
			{
				condition:
					hasNoServices &&
					isReviewerExpert &&
					location.pathname !== NavigationPath.solverExpertReview &&
					location.pathname !== NavigationPath.profile,
				redirect: () => (
					<Navigate
						to={NavigationPath.solverExpertReview}
						replace
					/>
				)
			},
			{
				condition:
					hasSolver &&
					location.pathname !== NavigationPath.solverSolve &&
					location.pathname !== NavigationPath.profile &&
					location.pathname !== NavigationPath.solverSolvedQuestions &&
					location.pathname !== NavigationPath.solverWhiteBoard &&
					location.pathname !== NavigationPath.solverResolveQuestions &&
					location.pathname !== NavigationPath.solverExpertReview &&
					location.pathname !== NavigationPath.messages &&
					location.pathname !== NavigationPath.supportDocuments &&
					location.pathname !== NavigationPath.boutiqueClassroom &&
					location.pathname !== NavigationPath.boutiqueStudents,
				redirect: () => (
					<Navigate
						to={NavigationPath.solverSolve}
						replace
					/>
				)
			}
		],
		[location.pathname, authData, isApplicant, completedApplicationSteps, completedApplicationStepsIsLoading, expertDetailInfo, expertDetailInfoIsLoading, weekCounts, LoadingWeekCounts, hasNoServices, isReviewerExpert]
	);

	const { data: findContractDetail } = useQueryProvider([QUERY_KEYS.findContract], () => ContractControllerApi.findContract(), {
		enabled: expertDetailInfo?.expert?.applicationStatus === 'APPROVED',
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false,
		onSuccess: response => {
			if (response?.contractLink) {
				setIsOpenContractLog(true);
			}
		}
	});

	const createContractLog = useMutationProvider(
		() =>
			ContractControllerApi.createContractLog({
				contractId: findContractDetail?.id
			}),
		{
			onError: error => {
				ExceptionManager.errorHandler(error);
				setIsLoadingContractLog(false);
			},
			onSuccess: () => {
				setIsOpenContractLog(false);
				setIsLoadingContractLog(false);
			}
		}
	);

	const activeRule = useMemo(() => navigationRules.find(rule => rule.condition), [navigationRules]);

	const onRegistrationAgreementAccept = async (): Promise<void> => {
		if (createContractLog.isLoading) {
			return;
		}

		setIsLoadingContractLog(true);
		await createContractLog.mutateAsync();
	};

	const onCloseAccountInformationModal = (): void => {
		setAppSettingsData({ ...appSettingsData, isOpenBankInformation: false });
	};

	useEffect(() => {
		if (authData?.access_token && userId) {
			if (isWebView()) {
				sendMessageToWebView({
					type: PostMessage.USER_CREDENTIALS,
					data: {
						userId
					}
				});
			} else {
				BrazeUtil?.initialize();
				Notification?.requestPermission();

				BrazeUtil?.init(userId);
				console.log('Braze initialized with user ID:', userId);
			}
		}
	}, [authData?.access_token, userId]);

	if (activeRule) {
		return activeRule.redirect();
	}

	return (
		<Fragment>
			<RegistrationAgreement
				onAccept={onRegistrationAgreementAccept}
				isLoading={isLoadingContractLog}
				isOpen={isOpenContractLog}
				pdfUrl={findContractDetail?.contractLink}
			/>
			<AccountInformationModal
				accountInformationData={expertDetailInfo?.paymentInfo}
				isOpen={appSettingsData?.isOpenBankInformation && !isOpenContractLog}
				onClose={onCloseAccountInformationModal}
			/>
			<NotificationProvider>
				<MeetingProvider>
					<Outlet />
				</MeetingProvider>
			</NotificationProvider>

			<Maintenance />
		</Fragment>
	);
};
